import React, { useRef } from 'react'
import dayjs from 'dayjs'
import { DateCalendar, DatePicker } from '@mui/x-date-pickers'
import {
  TextField,
  Box,
  Popover,
  IconButton,
  Divider,
  Chip,
  MenuItem,
  Stack,
  Select,
  SelectChangeEvent,
  FormControl,
  InputLabel
} from '@mui/material'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import ClearIcon from '@mui/icons-material/Clear'

interface CustomDateRangePickerProps {
  name: string
  label: string
  selectLabel?: string
  optionListName?: string
  extraProps?: Record<string, any>
  optionList?: Record<string, Array<Record<string, any>>>
  onChange?: (value: any) => void
  value?: {
    dateType: string
    startDate: string | null
    endDate: string | null
  }
  timeRangeName?: {
    start: string
    end: string
  }
  presetRanges?: {
    label: string
    days: number
  }[]
}

const CustomDateRangePicker: React.FC<CustomDateRangePickerProps> = ({
  name,
  label,
  optionListName,
  optionList = {},
  onChange,
  timeRangeName,
  presetRanges = [],
  value = { dateType: '', startDate: null, endDate: null }
}) => {
  const options = optionListName ? optionList[optionListName] || [] : []
  const startDateRef = useRef<HTMLInputElement>(null)
  const endDateRef = useRef<HTMLInputElement>(null)
  const [startDate, setStartDate] = React.useState<dayjs.Dayjs | null>(null)
  const [endDate, setEndDate] = React.useState<dayjs.Dayjs | null>(null)
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const [leftPanelMonth, setLeftPanelMonth] = React.useState(
    dayjs().subtract(1, 'month')
  )
  const [rightPanelMonth, setRightPanelMonth] = React.useState(dayjs())

  // internal date type value
  const [selectedRange, setSelectedRange] = React.useState(value?.dateType)

  // 处理 dateType 变化
  React.useEffect(() => {
    if (onChange && name) {
      onChange({
        name,
        value: selectedRange
      })
    }
  }, [selectedRange, onChange, name])

  // 处理 startDate 变化
  React.useEffect(() => {
    if (onChange && timeRangeName?.start) {
      onChange({
        name: timeRangeName.start,
        value: startDate ? startDate.format('YYYY-MM-DD') : null
      })
    }
  }, [startDate, onChange, timeRangeName?.start])

  // 处理 endDate 变化
  React.useEffect(() => {
    if (onChange && timeRangeName?.end) {
      onChange({
        name: timeRangeName.end,
        value: endDate ? endDate.format('YYYY-MM-DD') : null
      })
    }
  }, [endDate, onChange, timeRangeName?.end])

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    // Reset panel months when opening
    setLeftPanelMonth(dayjs().subtract(1, 'month'))
    setRightPanelMonth(dayjs())
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleDateSelect = (date: dayjs.Dayjs | null) => {
    if (!date) return

    if (!startDate && !endDate) {
      setStartDate(date)
      if (startDateRef.current) {
        startDateRef.current.value = date.format('YYYY-MM-DD')
      }
    } else if (startDate && !endDate) {
      if (date.isBefore(startDate)) {
        setEndDate(startDate)
        setStartDate(date)
        if (startDateRef.current) {
          startDateRef.current.value = date.format('YYYY-MM-DD')
        }
        if (endDateRef.current) {
          endDateRef.current.value = startDate.format('YYYY-MM-DD')
        }
      } else {
        setEndDate(date)
        if (endDateRef.current) {
          endDateRef.current.value = date.format('YYYY-MM-DD')
        }
      }
    } else if (startDate && endDate) {
      // Reset both dates and set the new date as startDate
      setStartDate(date)
      setEndDate(null)
      if (startDateRef.current) {
        startDateRef.current.value = date.format('YYYY-MM-DD')
      }
      if (endDateRef.current) {
        endDateRef.current.value = ''
      }
    }
  }

  const handleLeftMonthChange = (month: dayjs.Dayjs) => {
    setLeftPanelMonth(month)
    // If left panel would show same month as right panel, move right panel forward
    if (month.isSame(rightPanelMonth, 'month')) {
      setRightPanelMonth(month.add(1, 'month'))
    }
    // If left panel would be after right panel, move right panel forward
    else if (month.isAfter(rightPanelMonth)) {
      setRightPanelMonth(month.add(1, 'month'))
    }
  }

  const handleRightMonthChange = (month: dayjs.Dayjs) => {
    setRightPanelMonth(month)
    // If right panel would show same month as left panel, move left panel backward
    if (month.isSame(leftPanelMonth, 'month')) {
      setLeftPanelMonth(month.subtract(1, 'month'))
    }
    // If right panel would be before left panel, move left panel backward
    else if (month.isBefore(leftPanelMonth)) {
      setLeftPanelMonth(month.subtract(1, 'month'))
    }
  }

  const handlePresetRange = (days: number) => {
    const end = dayjs()
    const start = end.subtract(days, 'day')
    setStartDate(start)
    setEndDate(end)
    // Update refs
    if (startDateRef.current) {
      startDateRef.current.value = start.format('YYYY-MM-DD')
    }
    if (endDateRef.current) {
      endDateRef.current.value = end.format('YYYY-MM-DD')
    }
    // Adjust calendar panels to show the range
    if (start.isSame(end, 'month')) {
      // If start and end are in the same month, show next month in right panel
      setRightPanelMonth(end.add(1, 'month'))
      setLeftPanelMonth(start)
    } else {
      // If start and end are in different months, show their respective months
      setLeftPanelMonth(start.startOf('month'))
      setRightPanelMonth(end.startOf('month'))
    }
  }

  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    setSelectedRange(event.target.value)
  }

  const handleClearSelectDate = (event?: React.MouseEvent) => {
    event?.stopPropagation()
    setStartDate(null)
    setEndDate(null)
    // Clear ref values
    if (startDateRef.current) startDateRef.current.value = ''
    if (endDateRef.current) endDateRef.current.value = ''
    // Reset calendar panels
    setLeftPanelMonth(dayjs().subtract(1, 'month'))
    setRightPanelMonth(dayjs())
  }

  const handleClearSelect = (event: React.MouseEvent) => {
    event.stopPropagation()
    setSelectedRange('')
    handleClearSelectDate()
  }

  const isInRange = (date: dayjs.Dayjs) => {
    if (!startDate || !endDate) return false
    return date.isAfter(startDate, 'day') && date.isBefore(endDate, 'day')
  }

  const isDaySelected = (day: dayjs.Dayjs) => {
    if (day.isSame(startDate, 'day')) return true
    if (!endDate) return false
    return day.isSame(endDate, 'day')
  }

  const open = Boolean(anchorEl)

  return (
    <>
      <FormControl
        variant='outlined'
        fullWidth
        sx={{
          '& .MuiFormLabel-root': {
            backgroundColor: 'white',
            px: 1,
            '&.Mui-focused': {
              color: 'primary.main'
            }
          }
        }}>
        <InputLabel shrink>{label}</InputLabel>
        <Box
          sx={{
            border: '1px solid rgba(0, 0, 0, 0.23)',
            borderRadius: 1,
            '&:hover': {
              borderColor: 'rgba(0, 0, 0, 0.87)'
            },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 1.5,
            minHeight: '36px',
            height: '36px'
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
            <Box sx={{ position: 'relative', width: '278px' }}>
              <Select
                value={selectedRange}
                onChange={handleSelectChange}
                variant='standard'
                name={name}
                sx={{
                  width: '100%',
                  '& .MuiSelect-select': {
                    paddingY: 0,
                    border: 'none',
                    minWidth: '120px',
                    pr: selectedRange ? 4 : undefined
                  },
                  '&:before': {
                    display: 'none'
                  },
                  '&:after': {
                    display: 'none'
                  }
                }}
                MenuProps={{
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left'
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left'
                  },
                  PaperProps: {
                    sx: {
                      width: '278px',
                      marginLeft: '-12px',
                      marginTop: '8px'
                    }
                  }
                }}
                displayEmpty
                renderValue={value => options.find(i => i.id === value)?.name}>
                {options.map(option => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.name}
                  </MenuItem>
                ))}
              </Select>
              {selectedRange && (
                <IconButton
                  size='small'
                  sx={{
                    position: 'absolute',
                    right: '20px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    opacity: 0,
                    transition: 'opacity 0.2s',
                    padding: 0.5,
                    ':hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)'
                    },
                    '.MuiBox-root:hover &': {
                      opacity: 1
                    }
                  }}
                  onClick={handleClearSelect}>
                  <ClearIcon fontSize='small' />
                </IconButton>
              )}
            </Box>

            <Divider orientation='vertical' flexItem sx={{ my: '3px' }} />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DatePicker
                value={startDate}
                onChange={handleDateSelect}
                format='YYYY/MM/DD'
                slots={{
                  textField: params => (
                    <TextField
                      {...params}
                      variant='standard'
                      InputProps={{ disableUnderline: true }}
                      sx={{ width: '100px' }}
                    />
                  )
                }}
              />
              <span style={{ color: '#999999', fontSize: '8px' }}>—</span>
              <DatePicker
                value={endDate}
                onChange={handleDateSelect}
                format='YYYY/MM/DD'
                slots={{
                  textField: params => (
                    <TextField
                      {...params}
                      variant='standard'
                      InputProps={{ disableUnderline: true }}
                      sx={{ width: '100px' }}
                    />
                  )
                }}
              />
            </Box>
          </Box>
          {(startDate || endDate) && (
            <IconButton
              size='small'
              sx={{
                position: 'absolute',
                right: '50px',
                top: '50%',
                transform: 'translateY(-50%)',
                opacity: 0,
                transition: 'opacity 0.2s',
                padding: 0.5,
                ':hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)'
                },
                '.MuiBox-root:hover &': {
                  opacity: 1
                }
              }}
              onClick={handleClearSelectDate}>
              <ClearIcon fontSize='small' />
            </IconButton>
          )}

          <IconButton onClick={handleOpen} edge='end' sx={{ mr: -0.5 }}>
            <CalendarTodayIcon />
          </IconButton>
        </Box>
      </FormControl>

      <input type='hidden' name={timeRangeName?.start} ref={startDateRef} />
      <input type='hidden' name={timeRangeName?.end} ref={endDateRef} />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}>
        <Box>
          <Box sx={{ display: 'flex' }}>
            <DateCalendar
              key={`left-${leftPanelMonth.format('YYYY-MM')}-${startDate?.format('YYYY-MM-DD') || ''}-${endDate?.format('YYYY-MM-DD') || ''}`}
              referenceDate={leftPanelMonth}
              onMonthChange={handleLeftMonthChange}
              onChange={handleDateSelect}
              sx={{
                '& .MuiPickersYear-yearButton': {
                  fontSize: '14px'
                }
              }}
              slotProps={{
                day: ({ day }) => ({
                  selected: isDaySelected(day),
                  sx: {
                    ...(day.isSame(startDate, 'day') && {
                      backgroundColor: 'primary.main',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'primary.dark'
                      }
                    }),
                    ...(endDate &&
                      day.isSame(endDate, 'day') && {
                        backgroundColor: 'primary.main',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'primary.dark'
                        }
                      }),
                    ...(isInRange(day) && {
                      backgroundColor: 'primary.light',
                      opacity: 0.6,
                      '&:hover': {
                        backgroundColor: 'primary.main',
                        opacity: 0.8
                      }
                    })
                  }
                })
              }}
            />
            <DateCalendar
              key={`right-${rightPanelMonth.format('YYYY-MM')}-${startDate?.format('YYYY-MM-DD') || ''}-${endDate?.format('YYYY-MM-DD') || ''}`}
              referenceDate={rightPanelMonth}
              onMonthChange={handleRightMonthChange}
              onChange={handleDateSelect}
              sx={{
                '& .MuiPickersYear-yearButton': {
                  fontSize: '14px'
                }
              }}
              slotProps={{
                day: ({ day }) => ({
                  selected: isDaySelected(day),
                  sx: {
                    ...(day.isSame(startDate, 'day') && {
                      backgroundColor: 'primary.main',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'primary.dark'
                      }
                    }),
                    ...(endDate &&
                      day.isSame(endDate, 'day') && {
                        backgroundColor: 'primary.main',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'primary.dark'
                        }
                      }),
                    ...(isInRange(day) && {
                      backgroundColor: 'primary.light',
                      opacity: 0.6,
                      '&:hover': {
                        backgroundColor: 'primary.main',
                        opacity: 0.8
                      }
                    })
                  }
                })
              }}
            />
          </Box>
          <Stack direction='row' spacing={1} sx={{ mb: 2, px: 2 }}>
            {presetRanges.map(range => (
              <Chip
                key={range.days}
                label={range.label}
                onClick={() => handlePresetRange(range.days)}
                // variant='outlined'
                sx={{
                  '&:hover': {
                    opacity: 0.8
                  }
                }}
              />
            ))}
          </Stack>
        </Box>
      </Popover>
    </>
  )
}

export default CustomDateRangePicker
